import {Assignment} from '../types'
// src/services/assignmentService.ts

const generateRandomLocker = (): string => {
  const row = String.fromCharCode(65 + Math.floor(Math.random() * 3)); // A, B, or C
  const number = Math.floor(Math.random() * 10) + 1; // 1 to 10
  return `${row}${number}`;
};

const shouldReassignLocker = (oldAssignment: Assignment, newAssignment: Partial<Assignment>): boolean => {
  return ['parcelLength', 'parcelHeight', 'parcelWidth', 'needsCooling', 'needsHighSecurity'].some(
    field => newAssignment[field as keyof Assignment] !== undefined &&
              newAssignment[field as keyof Assignment] !== oldAssignment[field as keyof Assignment]
  );
};

const determineLockerSize = (width?: string, height?: string, length?: string): string => {
  if (!width || !height || !length) return '';

  const dimensions = [parseFloat(width), parseFloat(height), parseFloat(length)];
  const maxDimension = Math.max(...dimensions);

  if (maxDimension <= 40) return 'Small';
  if (maxDimension <= 80) return 'Medium';
  return 'Large';
};

const determineLockerType = (needsCooling?: boolean, needsHighSecurity?: boolean): string => {
  if (needsCooling) return 'Refrigerated';
  if (needsHighSecurity) return 'High-Security';
  return 'Regular';
};


const mockAssignments: Assignment[] = [
  {
    id: '1',
    date: new Date(2024, 8, 1),
    barcode: '1000000000',
    recipientName: 'Milan Bundalo',
    recipientPhone: '065 565 327',
    assignedLocker: 'A1',
    status: 'pending'
  },
  {
    id: '2',
    date: new Date(2024, 8, 2),
    barcode: '2000000000',
    recipientName: 'Shakira',
    recipientPhone: '065 234 567',
    assignedLocker: 'B3',
    status: 'pending'
  },
];

export const AssignmentService = {

  processCSV: async (csv: string): Promise<{ newAssignments: Assignment[], duplicates: number }> => {
    const lines = csv.split('\n');
    const headers = lines[0].split(';').map(header => header.trim());
    const newAssignments: Assignment[] = [];
    let duplicates = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';').map(value => value.trim());
      if (values.length !== headers.length) continue;

      const assignment = AssignmentService.createAssignmentFromCSV(headers, values);
      
      if (assignment) {
        const exists = await AssignmentService.assignmentExists(assignment.barcode);
        if (!exists) {
          newAssignments.push(await AssignmentService.createAssignment(assignment));
        } else {
          duplicates++;
        }
      }
    }

    return { newAssignments, duplicates };
  },

  createAssignmentFromCSV: (headers: string[], values: string[]): Omit<Assignment, 'id'> | null => {
    const assignment: Partial<Omit<Assignment, 'id'>> = {};
    let hasParcelDimensions = false;
    let hasLockerInfo = false;
    
    headers.forEach((header, index) => {
      const value = values[index];
      let a = new Date();
      console.log('Date is ' + a);
      switch (header) {
        case 'date':
          assignment.date = new Date( value );
          break;
        case 'barcode':
          assignment.barcode = value;
          break;
        case 'recipientName':
          assignment.recipientName = value;
          break;
        case 'recipientPhone':
          assignment.recipientPhone = value;
          break;
        case 'parcelWidth':
        case 'parcelHeight':
        case 'parcelLength':
          assignment[header] = value;
          hasParcelDimensions = true;
          break;
        case 'needsCooling':
          assignment.needsCooling = value.toLowerCase() === 'true';
          break;
        case 'needsHighSecurity':
          assignment.needsHighSecurity = value.toLowerCase() === 'true';
          break;
        case 'lockerSize':
        case 'lockerType':
          assignment[header] = value;
          hasLockerInfo = true;
          break;
      }
    });

    if (!assignment.barcode || !assignment.recipientName || !assignment.recipientPhone) {
      return null;
    }

    if (!hasParcelDimensions && !hasLockerInfo) {
      return null;
    }

    return assignment as Omit<Assignment, 'id'>;
  },

  assignmentExists: async (barcode: string): Promise<boolean> => {
    // In a real application, this would check against a database
    // For this example, we'll just return false to allow all new assignments
    return false;
  },

  getAssignments: async (): Promise<Assignment[]> => {
    // Simulating an API call with a delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockAssignments;
  },

  createAssignment: async (assignment: Omit<Assignment, 'id'>): Promise<Assignment> => {
    // Simulating an API call with a delay
    if (!assignment.lockerSize){
        assignment.lockerSize = determineLockerSize(assignment.parcelWidth, assignment.parcelHeight, assignment.parcelLength);
    }
    if (!assignment.lockerType){
      assignment.lockerType = determineLockerType(assignment.needsCooling, assignment.needsHighSecurity);
    }
    const newAssignment: Assignment = {
      id: Math.random().toString(16),
      ...assignment,
      assignedLocker: generateRandomLocker(),
    };
    console.log('Created new assignment with Id: ' + newAssignment.id);
    mockAssignments.push(newAssignment);
    return newAssignment;
  },

  updateAssignment: async (id: string, assignment: Partial<Assignment>): Promise<Assignment> => {
    const index = mockAssignments.findIndex(a => a.id === id);
    if (index === -1) {
      throw new Error('Assignment not found');
    }
    const oldAssignment = mockAssignments[index];
    
    if (shouldReassignLocker(oldAssignment, assignment)) {
      assignment.assignedLocker = generateRandomLocker();
    }
    
    mockAssignments[index] = { ...oldAssignment, ...assignment };
    return mockAssignments[index];
  },

  deleteAssignment: async (id: string): Promise<void> => {
    // Simulating an API call with a delay
    await new Promise(resolve => setTimeout(resolve, 500));
    const index = mockAssignments.findIndex(a => a.id === id);
    if (index === -1) {
      throw new Error('Assignment not found');
    }
    mockAssignments.splice(index, 1);
  },
};